import { createTheme, alpha } from '@mui/material/styles';

// Declare custom typography variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    cardTitle: React.CSSProperties;
    cardSubtitle: React.CSSProperties;
    badge: React.CSSProperties;
    statusText: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    cardTitle?: React.CSSProperties;
    cardSubtitle?: React.CSSProperties;
    badge?: React.CSSProperties;
    statusText?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    cardTitle: true;
    cardSubtitle: true;
    badge: true;
    statusText: true;
  }
}

// Declare custom button variants
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    pickup: true;
    dismiss: true;
    danger: true;
  }
}

// Declare custom card variants
declare module '@mui/material/Card' {
  interface CardPropsVariantOverrides {
    'student-waiting': true;
    'student-ready': true;
    'student-not-checked-in': true;
    'info-card': true;
  }
}

// Declare custom paper variants (for Paper components used as cards)
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    'student-waiting': true;
    'student-ready': true;
    'student-not-checked-in': true;
    'info-card': true;
  }
}

const theme = createTheme({
  // ============================================
  // COLOR PALETTE - Semantic Token System
  // ============================================
  palette: {
    mode: 'light',

    // Brand Colors
    primary: {
      main: '#00897B',      // Teal-600 (primary brand color)
      light: '#4DB6AC',     // Teal-300
      dark: '#00695C',      // Teal-800
      contrastText: '#FFFFFF',
    },

    secondary: {
      main: '#8D6E63',      // Brown-400
      light: '#BCAAA4',     // Brown-200
      dark: '#5D4037',      // Brown-700
      contrastText: '#FFFFFF',
    },

    // Status Colors (Semantic)
    // error = danger actions (delete, etc.)
    error: {
      main: '#EF5350',      // Red-400
      light: '#E57373',
      dark: '#C62828',      // Used for urgent wait times
      contrastText: '#FFFFFF',
    },

    // warning = waiting/pending states
    warning: {
      main: '#FB8C00',      // Orange-600 (primary waiting state)
      light: '#FFB74D',
      dark: '#ED6C02',      // Darker orange for text
      contrastText: '#FFFFFF',
    },

    // success = dismissed/ready/completed states
    success: {
      main: '#43A047',      // Green-600 (primary success state)
      light: '#66BB6A',     // Green-400
      dark: '#2E7D32',      // Green-800 for text
      contrastText: '#FFFFFF',
    },

    // info = informational elements
    info: {
      main: '#0288D1',      // Blue-600
      light: '#64B5F6',
      dark: '#01579B',
      contrastText: '#FFFFFF',
    },

    // UI Colors
    background: {
      default: '#FAFAFA',   // Light grey background
      paper: '#FFFFFF',     // White paper background
    },

    text: {
      primary: '#212121',   // Almost black
      secondary: '#757575', // Medium grey
      disabled: '#BDBDBD',  // Light grey
    },

    divider: '#E0E0E0',     // Border color
  },

  // ============================================
  // TYPOGRAPHY SYSTEM
  // ============================================
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',

    // Standard variants with responsive sizing
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
    },
    overline: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: 600,
    },

    // Custom variants for this application
    cardTitle: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    cardSubtitle: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      color: '#757575',
    },
    badge: {
      fontWeight: 600,
      fontSize: '0.75rem',
      lineHeight: 1.2,
      letterSpacing: '0.02em',
    },
    statusText: {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.4,
    },
  },

  // ============================================
  // SPACING & SHAPE
  // ============================================
  spacing: 8, // Base spacing unit (1 = 8px)

  shape: {
    borderRadius: 8, // Standard border radius
  },

  // ============================================
  // COMPONENT VARIANTS & OVERRIDES
  // ============================================
  components: {
    // ========== MuiPaper ==========
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default elevation background image
        },
      },
      variants: [
        // Student Waiting Paper (orange border + tint)
        {
          props: { variant: 'student-waiting' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.warning.main}`,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Student Ready/Dismissed Paper (green border + tint)
        {
          props: { variant: 'student-ready' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.success.main}`,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Student Not Checked In Paper (grey border + subtle tint)
        {
          props: { variant: 'student-not-checked-in' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.text.primary, 0.02),
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Info Paper (for summary cards/dashboard cards)
        {
          props: { variant: 'info-card' },
          style: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
      ],
    },

    // ========== MuiButton ==========
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '1rem',
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.8125rem',
        },
      },
      variants: [
        // Pickup Button Variant (for "I'm Here for Pickup")
        {
          props: { variant: 'pickup' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            fontWeight: 600,
            fontSize: '1rem',
            padding: '14px 28px',
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.success.main, 0.3),
              color: theme.palette.text.disabled,
            },
          }),
        },
        // Dismiss Button Variant (for "Mark as Dismissed")
        {
          props: { variant: 'dismiss' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            fontWeight: 600,
            fontSize: '0.9375rem',
            minHeight: 44,
            borderRadius: 8,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.success.main, 0.3),
              color: theme.palette.text.disabled,
            },
          }),
        },
        // Danger Button Variant (for delete actions)
        {
          props: { variant: 'danger' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          }),
        },
      ],
    },

    // ========== MuiCard ==========
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 0.3s ease',
        },
      },
      variants: [
        // Student Waiting Card (orange border + tint)
        {
          props: { variant: 'student-waiting' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.warning.main}`,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Student Ready/Dismissed Card (green border + tint)
        {
          props: { variant: 'student-ready' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.success.main}`,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Student Not Checked In Card (grey border + subtle tint)
        {
          props: { variant: 'student-not-checked-in' },
          style: ({ theme }) => ({
            border: `2px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.text.primary, 0.02),
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
        // Info Card (for summary cards/dashboard cards)
        {
          props: { variant: 'info-card' },
          style: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-2px)',
            },
          }),
        },
      ],
    },

    // ========== MuiChip ==========
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 600,
        },
      },
    },

    // ========== MuiTableCell ==========
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#FAFAFA',
          color: '#757575',
          fontSize: '0.875rem',
        },
        body: {
          fontSize: '0.875rem',
        },
      },
    },

    // ========== MuiAlert ==========
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
        },
        standardWarning: {
          backgroundColor: alpha('#FB8C00', 0.1),
        },
        standardSuccess: {
          backgroundColor: alpha('#43A047', 0.1),
        },
        standardError: {
          backgroundColor: alpha('#EF5350', 0.1),
        },
        standardInfo: {
          backgroundColor: alpha('#0288D1', 0.1),
        },
      },
    },

    // ========== MuiTextField ==========
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },

    // ========== MuiTooltip ==========
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.8125rem',
          borderRadius: 6,
          padding: '8px 12px',
        },
      },
    },
  },

  // ============================================
  // BREAKPOINTS (documented for reference)
  // ============================================
  breakpoints: {
    values: {
      xs: 0,      // Mobile portrait
      sm: 600,    // Mobile landscape
      md: 900,    // Tablet / Small desktop
      lg: 1200,   // Desktop
      xl: 1536,   // Large desktop
    },
  },
});

export default theme;
